import { Fragment, type ReactElement, useEffect, useMemo, useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"

import { Grid } from "@mui/material"
import { Button, NotificationUtils, Snackbar, Tab, Tabs } from "@synapse-analytics/synapse-ui"
import { v4 as uuidv4 } from "uuid"

import { BaseDrawer } from "../../../../../components/drawers/BaseDrawer"
import type { WorkflowInputFeature } from "../../../../../types/generated/api/WorkflowInputFeature"
import {
  convertCsvToJson,
  findDuplicateElementByProperty,
  readFileContentAsync,
} from "../../../../../utils/genericHelpers"
import { WorkflowSchemaContext } from "../../../context/Context"
import type {
  ConflictsListType,
  ConstantFeature,
  SchemaFeature,
  WorkflowSchemaDrawerProps,
} from "../../../types/interfaces"
import { checkIfValuesMatchTypes } from "../../../utils/workflowHelpers"
import { ConflictsList } from "./ConflictsList"
import { Constants } from "./Constants"
import { InputFeatures } from "./InputFeatures"
import { SchemaFileUpload } from "./SchemaFileUpload"
import { convertJsonToTableData, testJson } from "./helpers"

/**
 * WorkflowSchemaDialogContainer component that manages workflow schema configuration.
 * Handles file uploads, feature mapping, constants, and conflict resolution.
 */
export function WorkflowSchemaDialogContainer(props: Readonly<WorkflowSchemaDrawerProps>): ReactElement {
  // Prop destructuring
  const {
    open,
    onClose,
    formik,
    schema,
    isWorkflowsLoading,
    workflowName,
    isWorkflowInReadMode,
    isWorkflowDeprecated,
  } = props

  // Navigation hooks
  // Using this to get the current workflow state from the URL
  const [searchParams] = useSearchParams()

  // Translation hook
  const { t } = useTranslation("workflows", {
    keyPrefix: "workflowSchemaDialog",
  })

  // State hooks
  const [rowsPerPage, setRowsPerPage] = useState<number>(10)
  const [isJsonMode, setIsJsonMode] = useState<boolean>(false)
  const [jsonText, setJsonText] = useState<string>("")
  const [step, setStep] = useState<number>(0) // Tracks current step in the flow
  const [isNextStepEnabled, setIsNextStepEnabled] = useState<boolean>(false)
  const [acceptedFile, setAcceptedFile] = useState<File[]>([])
  const [conflictsList, setConflictsList] = useState<ConflictsListType>({
    unUsedFeatures: {},
    inUseFeatures: {},
  })

  const formikSnapShot = useRef<{
    features: Array<SchemaFeature>
    constants: Array<ConstantFeature>
  }>({
    features: JSON.parse(JSON.stringify(formik?.values?.features || [])),
    constants: JSON.parse(JSON.stringify(formik?.values?.constants || [])),
  })

  const [currentMode, setCurrentMode] = useState<"create" | "read" | "edit">(
    searchParams.get("workflowId") === "new" ? "create" : "read",
  )

  // State to track the active tab
  const [activeTab, setActiveTab] = useState<"Input Features" | "Constants">("Input Features")

  const { duplicateFeature, duplicateInputFeature, duplicateConstant } = useMemo(() => {
    const features = formik.values.features ?? []
    const constants = formik.values.constants ?? []

    let duplicateFeature: string | undefined = undefined
    let duplicateInputFeature: string | undefined = undefined
    let duplicateConstant: string | undefined = undefined

    if (features.length > 0 && constants.length > 0) {
      const featureNames = new Set(features.map((feature) => feature.name))

      for (const constant of constants) {
        const value = constant.name

        if (value !== "" && featureNames.has(value)) {
          duplicateFeature = value
          break
        }
      }
    }

    if (features.length > 0) {
      const duplicate = findDuplicateElementByProperty(features, "name")
      if (duplicate && duplicate?.name) duplicateInputFeature = duplicate.name
    }

    if (constants.length > 0) {
      const duplicate = findDuplicateElementByProperty(constants, "name")
      if (duplicate && duplicate?.name) duplicateConstant = duplicate.name
    }

    return {
      duplicateFeature,
      duplicateInputFeature,
      duplicateConstant,
    }
  }, [formik.values.features, formik.values.constants])

  /**
   * Resets the dialog states to its initial values.
   *
   * This function is called when closing the dialog
   */
  const resetStates = (): void => {
    setIsJsonMode(false)
    setJsonText("")

    setStep(0)
    setIsNextStepEnabled(false)
    setAcceptedFile([])
    setConflictsList({
      unUsedFeatures: {},
      inUseFeatures: {},
    })

    setActiveTab("Input Features")
  }

  /**
   * Handles closing the schema dialog based on the current mode.
   *
   * - In "create" mode:
   *   - Prevents closing if there are unresolved schema conflicts.
   *   - If in JSON mode, validates the JSON and updates constants if valid.
   * - In "edit" mode:
   *   - Resets the form to its previous snapshot (before editing) after a short delay.
   *   - Switches the dialog back to "read" mode.
   * - In all cases:
   *   - Resets dialog state after a short delay to avoid UI flicker during drawer transition.
   *   - Calls the provided onClose handler to close the dialog.
   */
  const handleAcceptDialogClose = (mode: "create" | "edit" | "read"): void => {
    if (mode === "create") {
      // If there are unresolved unused feature conflicts, prevent closing and show an error notification
      if (Object.keys(conflictsList.unUsedFeatures).length > 0)
        return NotificationUtils.toast(t("notifications.fileUpload.resolveConflicts"), {
          severity: "error",
        })

      if (isJsonMode) {
        const hasPassed = testJson(jsonText, t("notifications.schemaError.closeDialog"))

        if (!hasPassed) return

        formik.setFieldValue("constants", convertJsonToTableData(jsonText, formik.values.constants))
      }
    } else if (mode === "edit") {
      // In edit mode, reset the form to its previous snapshot after a short delay
      setTimeout(() => {
        formik.resetForm({
          values: {
            workflowName: workflowName,
            features: formikSnapShot.current.features ?? schema,
            constants: formikSnapShot.current.constants,
          },
        })
      }, 10)

      setCurrentMode("read")
    }

    // Reset dialog state after a short delay to avoid UI flicker during drawer transition
    setTimeout(() => {
      resetStates()
    }, 10)

    // Close the dialog
    onClose()
  }

  const handleSwitchTabs = (): void => {
    if (isJsonMode) {
      const hasPassed = testJson(jsonText, t("notifications.schemaError.switchTabs"))

      if (!hasPassed) return

      formik.setFieldValue("constants", convertJsonToTableData(jsonText, formik.values.constants))
    }

    setActiveTab("Input Features")
  }

  const handleSchemaSubmit = (): void => {
    const constantsToCheck = formik.values.constants?.map((constant) => {
      return { ...constant, name: constant.name.trim() }
    })

    // Check if the json is valid
    if (isJsonMode) {
      const hasPassed = testJson(jsonText, t("notifications.schemaError.saveSchema"))

      if (!hasPassed) return

      // Get the converted data directly instead of relying on formik state update
      formik.setFieldValue("constants", convertJsonToTableData(jsonText, constantsToCheck))
    }

    if (isWorkflowInReadMode) {
      // Check if the constants values match their types
      const isNotValidConstant = checkIfValuesMatchTypes(constantsToCheck)

      if (isNotValidConstant) {
        NotificationUtils.toast(t("notifications.invalidConstantValue", { name: isNotValidConstant }), {
          severity: "error",
        })

        return
      }

      // Check if there are any empty features or constants
      if (
        formik.values.features.some((feat) => feat.name === "") ||
        constantsToCheck.some((constant) => constant.name === "")
      ) {
        NotificationUtils.toast(t("notifications.emptyFeatures"), {
          severity: "error",
        })

        return
      }

      formik.setFieldValue("isSchemaSubmit", true)
      formik.submitForm()
      setTimeout(() => {
        onClose()
        setCurrentMode("read")
      }, 200)
    } else {
      onClose()
    }
  }

  /**
   * handleNextButton
   *
   * This async function is triggered when the user clicks the "Next" button after uploading a schema file.
   * It processes the uploaded file, detects conflicts with existing features, and updates the form state accordingly.
   */
  const handleNextButton = async (): Promise<void> => {
    if (!acceptedFile[0]) return

    // Read the content of the uploaded file asynchronously
    const rows = await readFileContentAsync(acceptedFile[0])

    // keeping only the first 2 columns
    const jsonConvertedRows = convertCsvToJson(rows).map((obj) => Object.fromEntries(Object.entries(obj).slice(0, 2)))

    // Get the list of existing feature names for conflict checking
    const oldFeaturesNames = formik.values.features.map((feature) => feature.name)

    // Temporary object to store conflicts found between new and existing features
    const tempConflictsList: ConflictsListType = {
      unUsedFeatures: {}, // Features that exist but are not currently used
      inUseFeatures: {}, // Features that exist and are currently used
    }

    // Array to collect new features that do not conflict with existing ones
    const newFeatures: SchemaFeature[] = []

    // Iterate over each feature from the uploaded file
    jsonConvertedRows.forEach((feature) => {
      const featureName = Object.values(feature)[0]
      const featureType = Object.values(feature)[1]

      // Skip if either name or type is missing
      if (!featureName || !featureType) return

      // Check if the feature name already exists in the current features
      const duplicateFeatureIndex = oldFeaturesNames.indexOf(featureName)

      const duplicateFeature = formik.values.features[duplicateFeatureIndex]

      if (duplicateFeatureIndex > -1) {
        // consider feature in conflict only if types are different
        if (duplicateFeature?.type !== featureType.toUpperCase()) {
          // If the feature exists, check if it is used or unused and add to the appropriate conflict list
          if (duplicateFeature?.is_used) {
            tempConflictsList.inUseFeatures[featureName] = {
              oldType: formik.values.features[duplicateFeatureIndex]?.type,
              newType: featureType.toUpperCase() as WorkflowInputFeature.type,
            }
          } else {
            tempConflictsList.unUsedFeatures[featureName] = {
              oldType: formik.values.features[duplicateFeatureIndex]?.type,
              newType: featureType.toUpperCase() as WorkflowInputFeature.type,
            }
          }
        }
      } else {
        // If the feature does not exist, add it to the new features array
        newFeatures.push({
          name: featureName,
          type: featureType.toUpperCase() as WorkflowInputFeature.type,
          is_required: false,
          remove: false,
          file: true,
          source: "workflow",
          id: uuidv4(),
          new: true,
        })
      }
    })

    // If there are new features, prepend them to the existing features in the form state
    if (newFeatures.length > 0) {
      formik.setFieldValue("features", [...newFeatures, ...formik.values.features])
    }

    // Reset the step and clear the accepted file
    setStep(0)
    setAcceptedFile([])

    // If there are no conflicts, show a success toast; otherwise, set the conflicts list for user resolution
    if (
      Object.keys(tempConflictsList.unUsedFeatures).length + Object.keys(tempConflictsList.inUseFeatures).length <=
      0
    ) {
      NotificationUtils.toast(t("notifications.fileUpload.schemaModified.description"), {
        title: t("notifications.fileUpload.schemaModified.title"),
        severity: "success",
      })
    } else {
      setConflictsList(tempConflictsList)
    }
  }

  const getActionButtons = (currentMode: "read" | "create" | "edit"): ReactElement => {
    if (step === 1) {
      return (
        <Fragment>
          <Button
            disabled={!formik.values.workflowName}
            variant="secondary"
            onClick={() => {
              setStep(0)
              setIsNextStepEnabled(false)
              setAcceptedFile([])
            }}
          >
            {t("buttons.cancel")}
          </Button>

          <Button disabled={!isNextStepEnabled} variant={"primary"} onClick={handleNextButton}>
            {t("buttons.next")}
          </Button>
        </Fragment>
      )
    }

    switch (currentMode) {
      case "read":
        return (
          <Button
            disabled={!formik.values.workflowName}
            variant="secondary"
            onClick={() => {
              setCurrentMode("edit")
              setRowsPerPage((prev) => (prev > 1 ? prev - 1 : prev)) // Adjust rows per page for edit mode
            }}
          >
            {t("buttons.edit")}
          </Button>
        )
      case "create":
        return (
          <Button
            disabled={
              Boolean(duplicateFeature || duplicateInputFeature || duplicateConstant) ||
              !formik.values.workflowName ||
              Object.keys(conflictsList.unUsedFeatures).length > 0
            }
            variant={"primary"}
            onClick={handleSchemaSubmit}
            isLoading={formik.isSubmitting}
          >
            {t("buttons.edit")}
          </Button>
        )
      case "edit":
        return (
          <Fragment>
            <Button
              disabled={!formik.values.workflowName}
              variant="secondary"
              onClick={() => handleAcceptDialogClose(currentMode)}
            >
              {t("buttons.cancel")}
            </Button>

            <Button
              disabled={
                Boolean(duplicateFeature || duplicateInputFeature || duplicateConstant) ||
                !formik.values.workflowName ||
                Object.keys(conflictsList.unUsedFeatures).length > 0
              }
              variant={"primary"}
              onClick={handleSchemaSubmit}
              isLoading={formik.isSubmitting}
            >
              {isWorkflowInReadMode ? t("buttons.save") : t("buttons.edit")}
            </Button>
          </Fragment>
        )

      default:
        return <Fragment></Fragment>
    }
  }

  const steps = [
    {
      component: (
        // Showing duplicate feature (between input features and constants) name
        <Grid item container xs direction={"column"} gap={2}>
          {duplicateFeature && (
            <Snackbar
              fullWidth
              severity="error"
              description={t("notifications.duplicateFeature.betweenTabs", {
                name: duplicateFeature,
              })}
            />
          )}

          {/* Showing duplicate feature name*/}
          {duplicateInputFeature && (
            <Snackbar
              severity="error"
              fullWidth
              description={t("notifications.duplicateFeature.inputFeatures", {
                name: duplicateFeature,
              })}
            />
          )}

          {/* Showing duplicate constant name*/}
          {duplicateConstant && (
            <Snackbar
              severity="error"
              fullWidth
              description={t("notifications.duplicateFeature.constants", {
                name: duplicateFeature,
              })}
            />
          )}

          {/* Tabs navigation */}
          <Grid item>
            {/* TODO:: should handle this variant from SUI */}
            <Tabs value={activeTab} className="base-tabs">
              <Tab
                key={"Input Features"}
                label={t("tabs.inputFeatures")}
                onClick={handleSwitchTabs}
                value={"Input Features"}
                selected={activeTab === "Input Features"}
                id={"Input Features"}
                size="large"
              />
              <Tab
                key={"Constants"}
                label={t("tabs.constants")}
                onClick={() => setActiveTab("Constants")}
                value={"Constants"}
                selected={activeTab === "Constants"}
                id={"Constants"}
                size="large"
              />
            </Tabs>
          </Grid>

          {Object.keys(conflictsList.unUsedFeatures).length + Object.keys(conflictsList.inUseFeatures).length > 0 &&
            activeTab === "Input Features" && (
              <Grid
                sx={{
                  "& button": {
                    width: "max-content",
                  },
                }}
              >
                <Snackbar
                  fullWidth
                  severity={"error"}
                  title={t("notifications.fileUpload.schemaConflictsFound.title")}
                  description={t("notifications.fileUpload.schemaConflictsFound.description")}
                  action={t("notifications.fileUpload.schemaConflictsFound.action")}
                  onAction={() => setStep(2)}
                />
              </Grid>
            )}

          {/* Tab content */}
          {activeTab === "Input Features" ? (
            <InputFeatures
              formik={formik}
              schema={schema}
              isWorkflowsLoading={isWorkflowsLoading}
              currentMode={currentMode}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
            />
          ) : (
            <Constants
              formik={formik}
              isWorkflowsLoading={isWorkflowsLoading}
              currentMode={currentMode}
              isJsonMode={isJsonMode}
              setIsJsonMode={setIsJsonMode}
              jsonText={jsonText}
              setJsonText={setJsonText}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
            />
          )}
        </Grid>
      ),
    },
    {
      component: <SchemaFileUpload />,
    },
    {
      component: (
        <ConflictsList
          formik={formik}
          features={formik.values.features.reduce((accumulator: { [key: string]: SchemaFeature }, currentFeature) => {
            accumulator[currentFeature.name] = currentFeature

            return accumulator
          }, {})}
        />
      ),
    },
  ]

  /**
   * Effect hook to determine and set the current mode based on the URL search parameters.
   *
   * - If `workflowId` is "new", the mode is set to "create".
   * - If `workflowId` exists and is not "new", the mode is set to "read".
   *
   * Dependencies:
   * - This effect runs whenever `searchParams` changes.
   */
  useEffect(() => {
    if (searchParams.get("workflowId") === "new") {
      setCurrentMode("create")
    } else if (searchParams.get("workflowId") && searchParams.get("workflowId") !== "new") {
      setCurrentMode("read")
    }
  }, [searchParams])

  return (
    <WorkflowSchemaContext.Provider
      value={{
        setStep,
        setIsNextStepEnabled,
        acceptedFile,
        setAcceptedFile,
        conflictsList,
        setConflictsList,
      }}
    >
      <BaseDrawer
        drawerProps={{
          open,
          onClose: (_, reason) => {
            if (reason === "backdropClick" || reason === "escapeKeyDown") {
              return undefined
            } else {
              handleAcceptDialogClose(currentMode)
            }
          },
          PaperProps: {
            style: {
              minWidth: "500px",
              width: "60%",
              maxWidth: "800px",
            },
          },
        }}
        onClose={() => {
          handleAcceptDialogClose(currentMode)
        }}
        title={
          step === 1
            ? currentMode === "create"
              ? t("title.fileUpload.createMode")
              : t("title.fileUpload.editMode", {
                  workflowName: workflowName,
                })
            : currentMode === "create"
              ? t("title.addSchema")
              : t("title.editSchema", {
                  workflowName: workflowName,
                })
        }
        footer={
          // Footer section with action buttons
          !isWorkflowDeprecated && step !== 2 ? (
            <Grid container justifyContent="flex-end" item xs={12} gap={1}>
              {getActionButtons(currentMode)}
            </Grid>
          ) : undefined
        }
      >
        {steps[step]?.component}
      </BaseDrawer>
    </WorkflowSchemaContext.Provider>
  )
}
